// Toast wrapper — the single place where an API error becomes display text.
//
// Why this exists: the API speaks two error shapes. Most controllers return
// the legacy `{ error: "some string" }`, but anything routed through
// middleware/errorHandler.js or the rate limiters returns the structured
// envelope `{ error: { code, message } }`. Call sites across the app are
// written as:
//
//     toast.error(err?.response?.data?.error || "Save failed")
//
// which is correct for the first shape and fatal for the second: the object is
// truthy, so the `||` fallback never fires, the object reaches sonner, and
// React throws #31 ("Objects are not valid as a React child"). That throw
// happens during render — outside the call site's try/catch — so the
// ErrorBoundary catches it and blanks the whole page instead of showing a
// toast.
//
// Coercing here rather than at ~45 call sites covers every transport at once
// (the default axios instance, the separate `api` instance from api/axios.js,
// and the handful of raw fetch calls), and keeps future call sites safe
// without anyone having to remember this note.
//
// Import `toast` from here instead of from "sonner". `Toaster` still comes
// straight from sonner in App.js.
import { isValidElement } from "react";
import { toast as sonnerToast } from "sonner";

const DEFAULT_MESSAGE = "Something went wrong";

// Keys an error-ish object might carry its human-readable text under, in the
// order we trust them.
const MESSAGE_KEYS = ["message", "error", "msg", "detail", "details", "title", "reason"];

/**
 * Reduce any value a call site might hand a toast into something React can
 * render. Strings, numbers and JSX pass through untouched; objects are mined
 * for their message; anything unrecognisable becomes `fallback`.
 *
 * Exported for tests and for the rare call site that needs the text itself.
 */
export const toMessage = (value, fallback = DEFAULT_MESSAGE, depth = 0) => {
  // Cyclic or absurdly nested input — stop rather than recurse forever.
  if (depth > 5) return fallback;

  if (value === null || value === undefined) return fallback;

  if (typeof value === "string") return value.trim() || fallback;
  if (typeof value === "number" || typeof value === "bigint") return String(value);
  if (typeof value === "boolean") return fallback;

  // sonner accepts a ReactNode or a render function for custom content —
  // leave both alone, they are not the bug this wrapper is here to fix.
  if (isValidElement(value) || typeof value === "function") return value;

  if (Array.isArray(value)) {
    const parts = value.map((item) => toMessage(item, "", depth + 1)).filter((p) => typeof p === "string" && p);
    return parts.length ? parts.join(", ") : fallback;
  }

  if (typeof value === "object") {
    // An axios error passed in whole: the useful part is the response body.
    if (value.response && typeof value.response === "object" && "data" in value.response) {
      const fromBody = toMessage(value.response.data, "", depth + 1);
      if (typeof fromBody === "string" && fromBody) return fromBody;
    }

    // A validation payload: `{ errors: [...] }` or `{ errors: { field: msg } }`.
    if (value.errors) {
      const fromErrors = toMessage(
        Array.isArray(value.errors) ? value.errors : Object.values(value.errors),
        "",
        depth + 1,
      );
      if (typeof fromErrors === "string" && fromErrors) return fromErrors;
    }

    for (const key of MESSAGE_KEYS) {
      if (!(key in value)) continue;
      const candidate = toMessage(value[key], "", depth + 1);
      if (typeof candidate === "string" && candidate) return candidate;
      // A JSX message on an object is still renderable.
      if (candidate && typeof candidate !== "string") return candidate;
    }

    return fallback;
  }

  return fallback;
};

// Wrap one sonner method so its first argument is always renderable. Each
// variant keeps its own fallback so a bare `toast.success(undefined)` still
// reads sensibly.
const wrap = (fn, fallback) => (value, options) => fn(toMessage(value, fallback), options);

// Start from sonner's own surface so anything we don't explicitly wrap
// (promise, custom, dismiss, loading, getHistory, getToasts…) keeps working
// exactly as before, then override the message-bearing variants.
export const toast = Object.assign(wrap(sonnerToast, DEFAULT_MESSAGE), sonnerToast, {
  error: wrap(sonnerToast.error, DEFAULT_MESSAGE),
  success: wrap(sonnerToast.success, "Done"),
  warning: wrap(sonnerToast.warning, DEFAULT_MESSAGE),
  info: wrap(sonnerToast.info, DEFAULT_MESSAGE),
  message: wrap(sonnerToast.message, DEFAULT_MESSAGE),
  loading: wrap(sonnerToast.loading, "Loading…"),
});

export default toast;
